import { sportGroups } from '../../lib/sport-groups';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { alert, haptic } from '../../lib/telegram';
import { getLang, localeOf, useT } from '../../lib/i18n';
import { api } from './api';

/**
 * Setting where a client already is.
 *
 * For someone you have coached for years who has just joined the app.
 * Editable whenever — this is a judgement, and judgements get revised.
 */
/**
 * Where the player started, as a card rather than a loose link: the
 * point their progress is measured from. Once set it says when, because
 * a starting point from last year reads differently from last week's.
 * The button stays Set either way: setting them again is how they are revised.
 */
/**
 * The client's sport: what they are offered on Book, and how their card
 * reads. Set by the link they joined through, else their first lesson; the
 * coach can change it here (PUT …/main-sport).
 */
export function ClientSport({ clientId, value, onChanged }: {
  clientId: string; value: 'padel' | 'tennis' | 'both' | null; onChanged: () => void;
}) {
  const t = useT();
  async function pick(sport: 'padel' | 'tennis' | 'both') {
    if (sport === value) return;
    haptic.tap();
    try {
      await api(`/api/coach/clients/${clientId}/main-sport`, { method: 'PUT', body: JSON.stringify({ sport }) });
      haptic.success();
      onChanged();
    } catch (err: any) {
      haptic.error();
      await alert(t('co.cd.couldNotSave', { message: err.message }));
    }
  }
  return (<>
    <div className="section-title">{t('co.set.sport')}</div>
    <div className="bseg" role="radiogroup" aria-label={t('co.set.sport')}>
      {(['padel', 'tennis', 'both'] as const).map((x) => (
        <button key={x} role="radio" aria-checked={value === x} onClick={() => pick(x)}>
          {t(x === 'padel' ? 'co.sport.padel' : x === 'tennis' ? 'co.sport.tennis' : 'co.sport.both')}
        </button>
      ))}
    </div>
    <div className="hot-hint">{value ? t('co.cd.sportHint') : t('co.cd.sportUnset')}</div>
  </>);
}

export function StartingScoresCard({ clientId, onOpen }: { clientId: string; onOpen: () => void }) {
  const t = useT();
  // undefined while loading, null when never set.
  const [setAt, setSetAt] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    api<{ created_at: string }[]>(`/api/coach/clients/${clientId}/baseline`)
      .then((rows) => setSetAt(rows.length
        ? rows.reduce((latest, r) => (r.created_at > latest ? r.created_at : latest), rows[0].created_at)
        : null))
      .catch(() => setSetAt(null));
  }, [clientId]);

  if (setAt === undefined) return null;
  const date = setAt
    ? new Intl.DateTimeFormat(localeOf(getLang()), { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(setAt))
    : null;

  return (
    <div className="card pp-start">
      <div className="pp-start__text">
        <span className="pp-start__title">{t('co.start.title')}</span>
        <span className="card__when">{date ? t('co.start.setOn', { date }) : t('co.start.explain')}</span>
      </div>
      <button className="pp-start__set" onClick={() => { haptic.tap(); onOpen(); }}>{t('co.start.set')}</button>
    </div>
  );
}

export function BaselineSheet({ clientId, name, coachSports, onClose }: {
  clientId: string; name: string; coachSports?: string[]; onClose: () => void;
}) {
  const t = useT();
  const [axes, setAxes] = useState<any[] | null>(null);
  const [scores, setScores] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api<any[]>('/api/rating-axes'),
      api<any[]>(`/api/coach/clients/${clientId}/baseline`),
    ]).then(([ax, existing]) => {
      setAxes(ax);
      const start: Record<string, number> = {};
      for (const a of ax) start[a.id] = 5;
      for (const e of existing) start[e.axis_id] = Number(e.score);
      setScores(start);
    }).catch(() => setAxes([]));
  }, [clientId]);

  async function save() {
    setBusy(true);
    try {
      await api(`/api/coach/clients/${clientId}/baseline`, {
        method: 'PUT', body: JSON.stringify({ scores }),
      });
      haptic.success();
      await alert(t('co.cd.saved'));
      onClose();
    } catch (err: any) {
      haptic.error();
      await alert(t('co.cd.couldNotSave', { message: err.message }));
    } finally { setBusy(false); }
  }

  return createPortal(
    <div className="sheet" onClick={onClose}>
      <div className="sheet__panel" onClick={(e) => e.stopPropagation()}>
        <div className="sheet__title">{t('co.cd.startTitle', { name })}</div>
        <div className="card__when">{t('co.cd.startExplain')}</div>

        {axes === null ? <div className="skeleton" style={{ marginTop: 12 }} /> : (
          <div style={{ marginTop: 14 }}>
            {/* By sport for a coach who teaches both (lib/sport-groups.ts). */}
            {sportGroups(axes, coachSports).map((g) => (<div key={g.sport ?? 'all'}>
            {g.sport && <div className="mf__label" style={{ margin: '4px 0 8px' }}>{t(g.sport === 'padel' ? 'co.sport.padel' : 'co.sport.tennis')}</div>}
            {g.items.map((a) => (
              <div key={a.id} style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                  <span style={{ fontSize: 14 }}>{a.name}</span>
                  <span className="daysheet__time">{scores[a.id] ?? 5}</span>
                </div>
                <input type="range" min={1} max={10} step={1} style={{ width: '100%' }}
                  value={scores[a.id] ?? 5}
                  onChange={(e) => setScores({ ...scores, [a.id]: Number(e.target.value) })} />
              </div>
            ))}
            </div>))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button className="paybar__opt" style={{ flex: 1 }} onClick={onClose}>{t('co.cd.back')}</button>
          <button className="paybar__opt"
            style={{ flex: 2, background: 'var(--accent)', color: 'var(--accent-text)', borderColor: 'var(--accent)' }}
            disabled={busy || !axes?.length} onClick={save}>
            {busy ? t('co.cd.saving') : t('co.cd.save')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
